import { useCallback, useEffect, useRef, useState } from "react";
import type { ParticipantColor } from "@/lib/participant-colors";
import { stepCursorSpring } from "@/lib/cursor-motion";

const SNAP_DISTANCE = 240;
const POSITION_EPSILON = 0.1;
const VELOCITY_EPSILON = 1;

interface SharedCursorProps {
  color: ParticipantColor;
  name: string;
  offsetX: number;
  offsetY: number;
  x: number;
  y: number;
}

export function SharedCursor({
  color,
  name,
  offsetX,
  offsetY,
  x,
  y,
}: SharedCursorProps) {
  const elementRef = useRef<HTMLSpanElement>(null);
  const [initialPosition] = useState({ x, y });
  const offset = useRef({ x: offsetX, y: offsetY });
  const motion = useRef({
    frame: 0,
    lastTime: 0,
    targetX: x,
    targetY: y,
    velocityX: 0,
    velocityY: 0,
    x,
    y,
  });

  const renderPosition = useCallback((nextX: number, nextY: number) => {
    if (!elementRef.current) return;
    elementRef.current.style.transform = `translate3d(${nextX + offset.current.x - 2}px, ${nextY + offset.current.y - 2}px, 0)`;
  }, []);

  useEffect(() => {
    offset.current = { x: offsetX, y: offsetY };
    renderPosition(motion.current.x, motion.current.y);
  }, [offsetX, offsetY, renderPosition]);

  useEffect(() => {
    const current = motion.current;
    current.targetX = x;
    current.targetY = y;
    const distance = Math.hypot(x - current.x, y - current.y);
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion || distance > SNAP_DISTANCE) {
      if (current.frame) cancelAnimationFrame(current.frame);
      current.frame = 0;
      current.velocityX = 0;
      current.velocityY = 0;
      current.x = x;
      current.y = y;
      renderPosition(x, y);
      return;
    }

    if (current.frame) return;
    current.lastTime = performance.now();

    function animate(time: number) {
      const current = motion.current;
      const deltaSeconds = Math.min((time - current.lastTime) / 1_000, 0.032);
      current.lastTime = time;

      const horizontal = stepCursorSpring(
        current.x,
        current.targetX,
        current.velocityX,
        deltaSeconds,
      );
      const vertical = stepCursorSpring(
        current.y,
        current.targetY,
        current.velocityY,
        deltaSeconds,
      );
      current.x = horizontal.position;
      current.y = vertical.position;
      current.velocityX = horizontal.velocity;
      current.velocityY = vertical.velocity;
      renderPosition(current.x, current.y);

      const settled =
        Math.abs(current.x - current.targetX) < POSITION_EPSILON &&
        Math.abs(current.y - current.targetY) < POSITION_EPSILON &&
        Math.abs(current.velocityX) < VELOCITY_EPSILON &&
        Math.abs(current.velocityY) < VELOCITY_EPSILON;
      if (settled) {
        current.frame = 0;
        return;
      }

      current.frame = requestAnimationFrame(animate);
    }
    current.frame = requestAnimationFrame(animate);
  }, [renderPosition, x, y]);

  useEffect(() => {
    const current = motion.current;
    return () => {
      if (current.frame) cancelAnimationFrame(current.frame);
    };
  }, []);

  return (
    <span
      className={`shared-cursor participant-${color}`}
      ref={elementRef}
      style={{
        transform: `translate3d(${initialPosition.x + offsetX - 2}px, ${initialPosition.y + offsetY - 2}px, 0)`,
      }}
    >
      <svg viewBox="0 0 24 30" aria-hidden="true">
        <path d="M2 2 20 17l-8 1 4 8-4 2-4-8-6 6Z" />
      </svg>
      <span className="shared-cursor-name">{name}</span>
    </span>
  );
}
